/**
 * MultiAddEvidenceStep — Step 4 of the log-activity multi-add flow.
 *
 * Thin wrapper around `EvidenceStep` that fetches activities by the
 * submitted `bulkImportIds` and derives the activity-id list. Back is
 * handled by the wizard chrome's footer (see ActivityFormFlow); the
 * in-card Skip / Attach controls live inside `EvidenceStep` itself.
 */

import { Loader2 } from 'lucide-react';
import { useGetActivitiesQuery } from '../../../api/emissions';
import type { EmissionEvidence } from '../../../types/emissions';
import type { UseUploadSessionResult } from '../../../hooks/useUploadSession';
import { EvidenceStep } from '../EvidenceStep';

const PAGE_SIZE = 100;

export interface MultiAddEvidenceStepProps {
  initiativeId: string;
  importIds: string[];
  uploadSession: UseUploadSessionResult;
  onContinue: (attached?: EmissionEvidence[]) => void;
}

export const MultiAddEvidenceStep = ({
  initiativeId,
  importIds,
  uploadSession,
  onContinue,
}: MultiAddEvidenceStepProps) => {
  const { data, isLoading } = useGetActivitiesQuery(
    {
      initiativeId,
      bulkImportIds: importIds.join(','),
      limit: PAGE_SIZE,
      offset: 0,
    },
    { skip: importIds.length === 0 },
  );

  const activityIds = (data?.data ?? []).map((a) => a._id);

  if (isLoading) {
    return (
      <div className='tw:flex tw:items-center tw:justify-center tw:p-8 tw:text-muted-foreground'>
        <Loader2 className='tw:size-4 tw:animate-spin tw:mr-2' />
        Loading activities…
      </div>
    );
  }

  return (
    <EvidenceStep
      initiativeId={initiativeId}
      activityIds={activityIds}
      uploadSession={uploadSession}
      onContinue={onContinue}
    />
  );
};
